import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import DynamicIcon from "../DynamicIcon";
import {
    createLead,
    updateLead,
    selectLeadDetails,
    selectLeadDetailsLoading,
    selectLeadCreateLoading,
    selectLeadCreateError,
    selectLeadUpdateLoading,
    selectLeadUpdateError,
    updateLeadStatus,
    convertLeadToCustomer,
    selectLeadConvertLoading,
    selectLeadConvertError,
    getLeadDetails
} from "../../redux/leads/leadsSlice";
import { closeModal, setHasToRefresh, setSwap, selectRightModalData } from "../../redux/rightModalSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import LoadingSection from "../LoadingSection";

const DEFAULT_LEAD_STATUSES = [
    { label: "Nuova", code: "nuova", color: "#3B82F6" },
    { label: "Contattata", code: "contattata", color: "#F59E0B" },
    { label: "Preventivo", code: "preventivo", color: "#8B5CF6" },
    { label: "Vinta", code: "vinta", color: "#10B981" },
    { label: "Persa", code: "persa", color: "#EF4444" },
];

export default function AddEditLead({ mode }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const leadDetails = useSelector(selectLeadDetails);
    const leadDetailsLoading = useSelector(selectLeadDetailsLoading);
    const createLoading = useSelector(selectLeadCreateLoading);
    const createError = useSelector(selectLeadCreateError);
    const updateLoading = useSelector(selectLeadUpdateLoading);
    const updateError = useSelector(selectLeadUpdateError);
    const convertLoading = useSelector(selectLeadConvertLoading);
    const convertError = useSelector(selectLeadConvertError);
    const rightModalData = useSelector(selectRightModalData);
    const prefillApplied = useRef(false);
    const [showConvertConfirm, setShowConvertConfirm] = useState(false);

    // Stati pipeline dall'account o default
    const leadStatuses = accountData?.settings?.leadStatus || DEFAULT_LEAD_STATUSES;

    // Lista collaboratori per il multi-select
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [selectedAssignedUsers, setSelectedAssignedUsers] = useState([]);

    // Cliente/Prospect collegato
    const [linkedCustomer, setLinkedCustomer] = useState(null);

    // Impianti del cliente
    const [systemsList, setSystemsList] = useState([]);
    const [systemOptions, setSystemOptions] = useState([]);
    const [selectedSystem, setSelectedSystem] = useState(null);
    const [selectedSystemData, setSelectedSystemData] = useState(null);
    const [systemsLoading, setSystemsLoading] = useState(false);

    // Tipi impianto (per lead senza impianto esistente)
    const [selectedSystemTypes, setSelectedSystemTypes] = useState([]);

    const [formData, setFormData] = useState({
        description: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        province: '',
        postalCode: '',
        notes: '',
        statusId: '',
        statusLabel: '',
        statusCode: '',
        statusColor: ''
    });

    const loadEmployees = useCallback(async () => {
        if (!accountData?._id || !token) return;
        try {
            const res = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/users/getEmployeesList?accountId=${accountData._id}`,
                { headers: { 'auth-token': token } }
            );
            const data = await res.json();
            const list = Array.isArray(data) ? data : (data.data || []);
            setEmployeeOptions(list.map(emp => ({
                value: emp._id,
                label: [emp.name, emp.surname].filter(Boolean).join(' '),
                color: emp.color || ''
            })));
        } catch { /* ignore */ }
    }, [accountData, token]);

    useEffect(() => {
        loadEmployees();
    }, [loadEmployees]);

    // Carica gli impianti (systems) del cliente selezionato
    const loadSystems = useCallback(async (customerId) => {
        if (!accountData?._id || !token || !customerId) {
            setSystemsList([]);
            setSystemOptions([]);
            setSelectedSystem(null);
            setSelectedSystemData(null);
            return [];
        }
        setSystemsLoading(true);
        try {
            const res = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/systems/systemsList?accountId=${accountData._id}&customerId=${customerId}`,
                { headers: { 'auth-token': token } }
            );
            const data = await res.json();
            const systems = Array.isArray(data) ? data : (data.data || []);
            setSystemsList(systems);
            const options = systems.map(s => ({
                value: s._id,
                label: `${s.code || ''} - ${s.title || 'Impianto'} (${s.customerAddressLabel || 'N/D'})`
            }));
            setSystemOptions(options);
            if (options.length === 1) {
                setSelectedSystem(options[0]);
                setSelectedSystemData(systems[0]);
            }
            return systems;
        } catch {
            setSystemsList([]);
            setSystemOptions([]);
            return [];
        } finally {
            setSystemsLoading(false);
        }
    }, [accountData, token]);

    // Ricerca clienti/prospect per l'autocomplete
    const searchCustomers = useCallback(async (inputValue) => {
        if (!accountData?._id || !token) return [];
        try {
            const res = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/customers/getForSelect2?accountId=${accountData._id}&searchTerm=${encodeURIComponent(inputValue)}&pagination=20`,
                { headers: { 'auth-token': token } }
            );
            const data = await res.json();
            return (data.results || []).map(c => ({
                value: c._id || c.id,
                label: c.title || c.text,
                prospect: !!c.prospect
            }));
        } catch {
            return [];
        }
    }, [accountData, token]);

    const loadCustomerOptions = (inputValue) => {
        return searchCustomers(inputValue);
    };

    const handleLinkedCustomerChange = async (selected) => {
        setLinkedCustomer(selected);
        setSelectedSystem(null);
        setSelectedSystemData(null);
        setSelectedSystemTypes([]);
        if (selected) {
            loadSystems(selected.value);
            // Pre-compila phone/email/address dai dati del cliente
            try {
                const res = await fetch(
                    `${import.meta.env.VITE_API_BASE_URL}/customers/getCustomerDetails?customerId=${selected.value}`,
                    { headers: { 'auth-token': token } }
                );
                const customerData = await res.json();
                if (customerData) {
                    const contact = customerData.contactsList?.[0];
                    const address = customerData.addresses?.[0];
                    setFormData(prev => ({
                        ...prev,
                        phone: prev.phone || contact?.phone || contact?.mobile || '',
                        email: prev.email || contact?.email || '',
                        address: prev.address || address?.address || ''
                    }));
                }
            } catch { /* ignora errore prefill */ }
        } else {
            setSystemOptions([]);
            setSystemsList([]);
        }
    };

    // Gestione selezione impianto
    const handleSystemChange = (selected) => {
        setSelectedSystem(selected);
        if (selected) {
            const fullSystem = systemsList.find(s => s._id === selected.value);
            setSelectedSystemData(fullSystem || null);
        } else {
            setSelectedSystemData(null);
        }
    };

    // Formato personalizzato per le opzioni del dropdown con badge
    const formatCustomerOptionLabel = (option) => (
        <div className="flex items-center gap-2">
            <span>{option.label}</span>
            {option.prospect ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                    Prospect
                </span>
            ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    Cliente
                </span>
            )}
        </div>
    );

    // Ripristina lo stato del form quando si torna da un swap (creazione prospect o impianto)
    const hasRestoredFromSwap = useRef(false);
    useEffect(() => {
        if (rightModalData?.savedLeadFormState && !hasRestoredFromSwap.current) {
            hasRestoredFromSwap.current = true;
            const saved = rightModalData.savedLeadFormState;
            setFormData(saved.formData || {});
            setSelectedAssignedUsers(saved.selectedAssignedUsers || []);
            setLinkedCustomer(saved.linkedCustomer || null);
            setSelectedSystem(saved.selectedSystem || null);
            setSelectedSystemData(saved.selectedSystemData || null);
            setSelectedSystemTypes(saved.selectedSystemTypes || []);

            // Se torniamo dalla creazione prospect, controlliamo se c'e' un nuovo prospect creato
            if (rightModalData.createdProspect) {
                const p = rightModalData.createdProspect;
                const label = p.business
                    ? (p.company || '')
                    : [p.name, p.surname].filter(Boolean).join(' ');
                const newCustomer = { value: p._id, label, prospect: true };
                setLinkedCustomer(newCustomer);
                setSelectedSystem(null);
                setSelectedSystemData(null);
            }
            // Ripristina gli impianti del cliente salvato
            else if (saved.linkedCustomer) {
                loadSystems(saved.linkedCustomer.value);
            }
        }
    }, [rightModalData]);

    // Popola il form in modalita' modifica
    useEffect(() => {
        if (mode === 'edit' && leadDetails?._id && !rightModalData?.savedLeadFormState) {
            setFormData({
                description: leadDetails.description || '',
                phone: leadDetails.phone || '',
                email: leadDetails.email || '',
                address: leadDetails.address || '',
                city: leadDetails.city || '',
                province: leadDetails.province || '',
                postalCode: leadDetails.postalCode || '',
                notes: leadDetails.notes || '',
                statusId: leadDetails.statusId || '',
                statusLabel: leadDetails.statusLabel || '',
                statusCode: leadDetails.statusCode || '',
                statusColor: leadDetails.statusColor || ''
            });

            if (leadDetails.assignedUsers && leadDetails.assignedUsers.length > 0) {
                setSelectedAssignedUsers(leadDetails.assignedUsers.map(u => ({
                    value: u.userId,
                    label: u.userLabel,
                    color: u.userColor || ''
                })));
            }

            // Tipi impianto
            if (leadDetails.systemTypes && leadDetails.systemTypes.length > 0) {
                setSelectedSystemTypes(leadDetails.systemTypes.map(st => ({
                    value: st.systemTypeId,
                    label: st.systemTypeLabel
                })));
            }

            if (leadDetails.linkedCustomerId) {
                const customerOption = {
                    value: leadDetails.linkedCustomerId,
                    label: leadDetails.linkedCustomerLabel || '',
                    prospect: !!leadDetails.linkedCustomerIsProspect
                };
                setLinkedCustomer(customerOption);
                loadSystems(leadDetails.linkedCustomerId).then((loadedSystems) => {
                    if (leadDetails.linkedMaintenanceId) {
                        const fullSystem = loadedSystems.find(s => s._id === leadDetails.linkedMaintenanceId);
                        setSelectedSystem({
                            value: leadDetails.linkedMaintenanceId,
                            label: leadDetails.linkedMaintenanceLabel || ''
                        });
                        setSelectedSystemData(fullSystem || null);
                    }
                });
            }
        }
    }, [mode, leadDetails]);

    // Prefill da navigazione (bottone "Crea Lead" dalla scheda cliente)
    useEffect(() => {
        if (mode === 'add' && rightModalData?.prefillFromCustomer && !prefillApplied.current) {
            prefillApplied.current = true;
            const c = rightModalData.prefillFromCustomer;
            const customerOption = {
                value: c.customerId,
                label: c.label,
                prospect: !!c.isProspect
            };
            setLinkedCustomer(customerOption);
            loadSystems(c.customerId);
        }
    }, [mode, rightModalData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Salva lo stato corrente del form per il ripristino dopo lo swap
    const getCurrentFormState = () => ({
        formData,
        selectedAssignedUsers,
        linkedCustomer,
        selectedSystem,
        selectedSystemData,
        selectedSystemTypes
    });

    // Apre AddEditCustomer nella stessa RightModal tramite setSwap
    const handleOpenNewProspect = () => {
        dispatch(setSwap({
            mode: 'add_prospect',
            isOld: true,
            dataToSave: {
                savedLeadFormState: getCurrentFormState(),
                leadMode: mode
            }
        }));
    };

    // Cambio stato dalla pipeline (solo edit)
    const handleStatusChange = (status) => {
        if (leadDetails?._id) {
            dispatch(updateLeadStatus({
                leadId: leadDetails._id,
                statusId: status._id || '',
                statusLabel: status.label,
                statusCode: status.code,
                statusColor: status.color,
                token
            })).then((result) => {
                if (!result.error) {
                    setFormData(prev => ({
                        ...prev,
                        statusId: status._id || '',
                        statusLabel: status.label,
                        statusCode: status.code,
                        statusColor: status.color
                    }));
                    dispatch(getLeadDetails({ leadId: leadDetails._id, token }));
                    dispatch(setHasToRefresh());
                }
            });
        }
    };

    // Conversione prospect → cliente
    const confirmConvert = () => {
        if (leadDetails?._id) {
            dispatch(convertLeadToCustomer({
                leadId: leadDetails._id,
                token
            })).then((result) => {
                if (!result.error) {
                    setShowConvertConfirm(false);
                    dispatch(getLeadDetails({ leadId: leadDetails._id, token }));
                    dispatch(setHasToRefresh());
                }
            });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        let leadToSave = { ...formData };
        if (mode === 'add') {
            const defaultStatus = leadStatuses[0] || DEFAULT_LEAD_STATUSES[0];
            leadToSave.statusId = defaultStatus._id || '';
            leadToSave.statusLabel = defaultStatus.label;
            leadToSave.statusCode = defaultStatus.code;
            leadToSave.statusColor = defaultStatus.color;
        }

        leadToSave.assignedUsers = selectedAssignedUsers.map(u => ({
            userId: u.value,
            userLabel: u.label,
            userColor: u.color || ''
        }));

        if (linkedCustomer) {
            leadToSave.linkedCustomerId = linkedCustomer.value;
            leadToSave.linkedCustomerLabel = linkedCustomer.label;
            leadToSave.linkedCustomerIsProspect = !!linkedCustomer.prospect;
        } else {
            leadToSave.linkedCustomerId = '';
            leadToSave.linkedCustomerLabel = '';
            leadToSave.linkedCustomerIsProspect = false;
        }

        if (selectedSystem) {
            leadToSave.linkedMaintenanceId = selectedSystem.value;
            leadToSave.linkedMaintenanceLabel = selectedSystem.label;
            // Impianto esistente selezionato: non servono indirizzo e tipi impianto separati
            leadToSave.address = '';
            leadToSave.city = '';
            leadToSave.province = '';
            leadToSave.postalCode = '';
            leadToSave.systemTypes = [];
        } else {
            leadToSave.linkedMaintenanceId = '';
            leadToSave.linkedMaintenanceLabel = '';
            // Nessun impianto: salva tipi impianto selezionati
            leadToSave.systemTypes = selectedSystemTypes.map(st => ({
                systemTypeId: st.value,
                systemTypeLabel: st.label
            }));
        }

        if (mode === 'add') {
            dispatch(createLead({
                lead: { ...leadToSave, accountId: accountData._id },
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(setHasToRefresh());
                    dispatch(closeModal());
                }
            });
        } else {
            dispatch(updateLead({
                lead: { ...leadToSave, _id: leadDetails._id, accountId: leadDetails.accountId },
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(setHasToRefresh());
                    dispatch(closeModal());
                }
            });
        }
    };

    if (mode === 'edit' && leadDetailsLoading) {
        return <LoadingSection />;
    }

    const inputClass = "w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">
                {mode === 'add' ? 'Nuova Lead' : 'Modifica Lead'}
            </h2>

            {(createLoading || updateLoading || convertLoading) && <LoadingSection isFixed />}

            {/* Pipeline stati e conversione (solo in edit) */}
            {mode === 'edit' && leadDetails?._id && (
                <>
                    <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold mb-3">Pipeline</h3>
                        <div className="flex flex-wrap gap-2">
                            {leadStatuses.map((status, index) => {
                                const isActive = formData.statusCode === status.code;
                                return (
                                    <button
                                        key={index}
                                        type="button"
                                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${isActive ? 'ring-2 ring-offset-2' : 'opacity-60 hover:opacity-100'}`}
                                        style={{
                                            backgroundColor: isActive ? status.color + '20' : 'transparent',
                                            color: status.color,
                                            borderColor: status.color,
                                            ringColor: status.color
                                        }}
                                        onClick={() => handleStatusChange(status)}
                                        disabled={!!leadDetails.convertedToCustomerId}
                                    >
                                        {status.label}
                                    </button>
                                );
                            })}
                        </div>
                        {leadDetails.convertedToCustomerId && (
                            <p className="text-sm text-green-600 mt-3">
                                <DynamicIcon iconClass="fas fa-check-circle" /> Convertita in cliente
                            </p>
                        )}
                    </div>

                    {/* Bottone Converti in Cliente */}
                    {!leadDetails.convertedToCustomerId && formData.statusCode !== 'persa' && (
                        <div className="mb-6">
                            <button
                                type="button"
                                className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                                onClick={() => setShowConvertConfirm(true)}
                            >
                                <DynamicIcon iconClass="fas fa-user-plus" /> Converti in Cliente
                            </button>
                        </div>
                    )}
                </>
            )}

            <form onSubmit={handleSubmit}>

                {/* Collegamento Cliente/Prospect */}
                <h3 className="text-lg font-semibold mb-3">Collega a Cliente / Prospect</h3>
                <div className="mb-6">
                    <div className="flex gap-2 items-start">
                        <div className="flex-1">
                            <AsyncSelect
                                cacheOptions
                                defaultOptions
                                loadOptions={loadCustomerOptions}
                                value={linkedCustomer}
                                onChange={handleLinkedCustomerChange}
                                formatOptionLabel={formatCustomerOptionLabel}
                                placeholder="Cerca cliente o prospect..."
                                noOptionsMessage={() => "Nessun risultato"}
                                loadingMessage={() => "Ricerca..."}
                                isClearable
                                styles={{
                                    control: (base) => ({ ...base, borderColor: '#d1d5db', borderRadius: '0.375rem', minHeight: '42px' }),
                                }}
                            />
                        </div>
                        <button
                            type="button"
                            className="px-3 py-2 rounded-md text-sm font-medium text-white border-0 whitespace-nowrap"
                            style={{ backgroundColor: '#f97316', minHeight: '42px' }}
                            onClick={handleOpenNewProspect}
                        >
                            <DynamicIcon iconClass="fi fi-br-user-add" /> Nuovo Prospect
                        </button>
                    </div>
                </div>

                {/* Seleziona Impianto (solo se cliente, non prospect) */}
                {linkedCustomer && !linkedCustomer.prospect && (
                    <>
                        <h3 className="text-lg font-semibold mb-3">Impianto</h3>
                        <div className="mb-6">
                            {systemsLoading ? (
                                <p className="text-sm text-gray-500">Caricamento impianti...</p>
                            ) : systemOptions.length > 0 ? (
                                <Select
                                    options={systemOptions}
                                    value={selectedSystem}
                                    onChange={handleSystemChange}
                                    placeholder="Seleziona un impianto..."
                                    noOptionsMessage={() => "Nessun impianto trovato"}
                                    isClearable
                                    styles={{
                                        control: (base) => ({ ...base, borderColor: '#d1d5db', borderRadius: '0.375rem', minHeight: '42px' }),
                                    }}
                                />
                            ) : (
                                <p className="text-sm text-gray-500">Nessun impianto associato a questo cliente.</p>
                            )}
                        </div>
                    </>
                )}

                {/* Contatti */}
                <h3 className="text-lg font-semibold mb-3 mt-6">Contatti</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                            Telefono
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Telefono..."
                        />
                    </div>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Email..."
                        />
                    </div>
                </div>

                {/* Indirizzo e Tipi Impianto (solo se NON c'e' un impianto esistente selezionato) */}
                {!selectedSystem && (
                    <>
                        <h3 className="text-lg font-semibold mb-3 mt-6">Indirizzo</h3>
                        {selectedSystem === null && linkedCustomer && !linkedCustomer.prospect && systemOptions.length > 0 && (
                            <p className="text-xs text-gray-400 mb-2">
                                Compila l'indirizzo solo se la lead riguarda un nuovo impianto non ancora censito.
                            </p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="md:col-span-2">
                                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                                    Indirizzo
                                </label>
                                <input
                                    type="text"
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    className={inputClass}
                                    placeholder="Via, numero civico..."
                                />
                            </div>
                            <div>
                                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                                    Citta'
                                </label>
                                <input
                                    type="text"
                                    id="city"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    className={inputClass}
                                    placeholder="Citta'..."
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-1">
                                        Provincia
                                    </label>
                                    <input
                                        type="text"
                                        id="province"
                                        name="province"
                                        value={formData.province}
                                        onChange={handleInputChange}
                                        className={inputClass}
                                        placeholder="Es. MI"
                                        maxLength={2}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                                        CAP
                                    </label>
                                    <input
                                        type="text"
                                        id="postalCode"
                                        name="postalCode"
                                        value={formData.postalCode}
                                        onChange={handleInputChange}
                                        className={inputClass}
                                        placeholder="CAP..."
                                        maxLength={5}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Tipi Impianto */}
                        {accountData?.settings?.systemTypes && accountData.settings.systemTypes.length > 0 && (
                            <>
                                <h3 className="text-lg font-semibold mb-3 mt-6">Tipo Impianto</h3>
                                <div className="mb-4">
                                    <Select
                                        isMulti
                                        options={(accountData.settings.systemTypes || []).map(st => ({
                                            value: st._id,
                                            label: st.label
                                        }))}
                                        value={selectedSystemTypes}
                                        onChange={(selected) => setSelectedSystemTypes(selected || [])}
                                        placeholder="Seleziona tipo/i impianto..."
                                        noOptionsMessage={() => "Nessun tipo disponibile"}
                                        styles={{
                                            control: (base) => ({ ...base, borderColor: '#d1d5db', borderRadius: '0.375rem', minHeight: '42px' }),
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}

                {/* Descrizione e Note */}
                <h3 className="text-lg font-semibold mb-3 mt-6">Dettagli Lead</h3>
                <div className="grid grid-cols-1 gap-4">
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            Descrizione
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Descrizione della lead..."
                            rows="3"
                        />
                    </div>
                    <div>
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                            Note
                        </label>
                        <textarea
                            id="notes"
                            name="notes"
                            value={formData.notes}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Note..."
                            rows="2"
                        />
                    </div>
                </div>

                {/* Collaboratori Assegnati */}
                <h3 className="text-lg font-semibold mb-3 mt-6">Collaboratori Assegnati</h3>
                <Select
                    isMulti
                    options={employeeOptions}
                    value={selectedAssignedUsers}
                    onChange={(selected) => setSelectedAssignedUsers(selected || [])}
                    placeholder="Seleziona collaboratori..."
                    noOptionsMessage={() => "Nessun collaboratore disponibile"}
                    styles={{
                        control: (base) => ({ ...base, borderColor: '#d1d5db', borderRadius: '0.375rem', minHeight: '42px' }),
                        multiValue: (base, { data }) => ({
                            ...base,
                            backgroundColor: (data.color || '#6B7280') + '20',
                            borderRadius: '9999px'
                        }),
                        multiValueLabel: (base, { data }) => ({
                            ...base,
                            color: data.color || '#6B7280',
                            fontWeight: 500
                        }),
                        multiValueRemove: (base, { data }) => ({
                            ...base,
                            color: data.color || '#6B7280',
                            ':hover': { backgroundColor: (data.color || '#6B7280') + '30', color: data.color || '#374151' }
                        })
                    }}
                />

                {/* Messaggio di errore */}
                {(createError || updateError) && (
                    <div className="mb-4 mt-4 p-2 bg-red-100 text-red-700 rounded-md">
                        <p>{createError || updateError}</p>
                    </div>
                )}

                {/* Bottoni azioni */}
                <div className="flex justify-end space-x-3 mt-8">
                    <button
                        type="button"
                        className="secondary"
                        onClick={() => dispatch(closeModal())}
                        disabled={createLoading || updateLoading}
                    >
                        Annulla
                    </button>

                    <button
                        type="submit"
                        className="primary"
                        disabled={createLoading || updateLoading}
                    >
                        {createLoading || updateLoading ? (
                            <>
                                <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                Salvataggio...
                            </>
                        ) : (
                            <>
                                <DynamicIcon iconClass="fas fa-save" />
                                Salva
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Dialog conferma conversione */}
            {showConvertConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4">Converti in Cliente</h3>
                        <p className="mb-6">
                            {leadDetails?.linkedCustomerId && leadDetails?.linkedCustomerIsProspect ? (
                                <>Sei sicuro di voler convertire questa lead? Il prospect collegato <strong>{leadDetails.linkedCustomerLabel}</strong> diventerà cliente.</>
                            ) : leadDetails?.linkedCustomerId && !leadDetails?.linkedCustomerIsProspect ? (
                                <>Sei sicuro di voler convertire questa lead? La lead è già collegata al cliente <strong>{leadDetails.linkedCustomerLabel}</strong>, verrà solo aggiornato lo stato.</>
                            ) : (
                                <>Sei sicuro di voler convertire questa lead in un nuovo cliente? Verrà creato un cliente con i dati della lead.</>
                            )}
                        </p>

                        {convertError && (
                            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md">
                                <p>{convertError}</p>
                            </div>
                        )}

                        <div className="flex justify-end space-x-3">
                            <button
                                type="button"
                                className="secondary"
                                onClick={() => setShowConvertConfirm(false)}
                                disabled={convertLoading}
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                className="primary"
                                onClick={confirmConvert}
                                disabled={convertLoading}
                            >
                                {convertLoading ? (
                                    <>
                                        <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                        Conversione...
                                    </>
                                ) : (
                                    <>
                                        <DynamicIcon iconClass="fas fa-user-plus" />
                                        Converti
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
